import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { format, startOfMonth, eachMonthOfInterval, subMonths } from "date-fns";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Claim {
  id: string;
  claim_type: string;
  status: "pending" | "in_review" | "approved" | "denied";
  settlement_amount: number | null;
  created_at: string;
}

interface MonthlyData {
  month: string;
  pending: number;
  in_review: number;
  approved: number;
  denied: number;
  total: number;
}

interface StatusData {
  name: string;
  value: number;
}

interface TypeData {
  name: string;
  count: number;
}

const COLORS = {
  pending: "#94a3b8",
  in_review: "#60a5fa",
  approved: "#22c55e",
  denied: "#ef4444",
};

const Analytics = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [claims, setClaims] = useState<Claim[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
        if (!session?.user) {
          navigate("/auth");
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (!session?.user) {
        navigate("/auth");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (user) {
      checkAdminStatus();
    }
  }, [user]);

  const checkAdminStatus = async () => {
    try {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user?.id)
        .eq("role", "admin")
        .maybeSingle();

      if (error) throw error;
      
      if (!data) {
        toast.error("Access denied: Admin privileges required");
        navigate("/dashboard");
        return;
      }

      setIsAdmin(true);
      fetchClaims();
    } catch (error: any) {
      console.error("Error checking admin status:", error);
      toast.error("Failed to verify admin access");
      navigate("/dashboard");
    }
  };

  const fetchClaims = async () => {
    try {
      const { data, error } = await supabase
        .from("claims")
        .select("id, claim_type, status, settlement_amount, created_at")
        .order("created_at", { ascending: true });

      if (error) throw error;

      setClaims(data || []);
    } catch (error: any) {
      toast.error("Failed to load claims data");
      console.error("Error fetching claims:", error);
    } finally {
      setLoading(false);
    }
  };

  const getMonthlyData = (): MonthlyData[] => {
    const last6Months = eachMonthOfInterval({
      start: subMonths(new Date(), 5),
      end: new Date(),
    });

    return last6Months.map((month) => {
      const monthStart = startOfMonth(month);
      const monthEnd = startOfMonth(subMonths(month, -1));
      
      const monthClaims = claims.filter((claim) => {
        const claimDate = new Date(claim.created_at);
        return claimDate >= monthStart && claimDate < monthEnd;
      });

      return {
        month: format(month, "MMM yyyy"),
        pending: monthClaims.filter((c) => c.status === "pending").length,
        in_review: monthClaims.filter((c) => c.status === "in_review").length,
        approved: monthClaims.filter((c) => c.status === "approved").length,
        denied: monthClaims.filter((c) => c.status === "denied").length,
        total: monthClaims.length,
      };
    });
  };

  const getStatusData = (): StatusData[] => {
    const statusCounts = claims.reduce((acc, claim) => {
      acc[claim.status] = (acc[claim.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(statusCounts).map(([status, count]) => ({
      name: status.split("_").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" "),
      value: count,
    }));
  };

  const getTypeData = (): TypeData[] => {
    const typeCounts = claims.reduce((acc, claim) => {
      acc[claim.claim_type] = (acc[claim.claim_type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(typeCounts).map(([type, count]) => ({
      name: type,
      count,
    }));
  };

  const getSettlementData = () => {
    const last6Months = eachMonthOfInterval({
      start: subMonths(new Date(), 5),
      end: new Date(),
    });

    return last6Months.map((month) => {
      const monthStart = startOfMonth(month);
      const monthEnd = startOfMonth(subMonths(month, -1));
      
      const monthClaims = claims.filter((claim) => {
        const claimDate = new Date(claim.created_at);
        return claimDate >= monthStart && claimDate < monthEnd && claim.settlement_amount;
      });

      const totalAmount = monthClaims.reduce((sum, claim) => sum + (claim.settlement_amount || 0), 0);
      const avgAmount = monthClaims.length > 0 ? totalAmount / monthClaims.length : 0;

      return {
        month: format(month, "MMM yyyy"),
        total: totalAmount,
        average: Math.round(avgAmount),
        count: monthClaims.length,
      };
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  const monthlyData = getMonthlyData();
  const statusData = getStatusData();
  const typeData = getTypeData();
  const settlementData = getSettlementData();

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      <main className="flex-1 container mx-auto px-4 py-8 mt-20">
        <Button
          variant="ghost"
          onClick={() => navigate("/admin")}
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Admin Panel
        </Button>

        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Analytics Dashboard</h1>
          <p className="text-muted-foreground">
            Visualize claim trends and insights over time
          </p>
        </div>

        <div className="space-y-6">
          {/* Claims Over Time */}
          <Card>
            <CardHeader>
              <CardTitle>Claims Over Time</CardTitle>
              <CardDescription>Monthly claim submissions for the last 6 months</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="month" 
                    className="text-xs"
                    tick={{ fill: 'hsl(var(--foreground))' }}
                  />
                  <YAxis 
                    className="text-xs"
                    tick={{ fill: 'hsl(var(--foreground))' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--background))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '6px'
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="total" 
                    stroke="hsl(var(--primary))" 
                    strokeWidth={2}
                    name="Total Claims"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="approved" 
                    stroke={COLORS.approved} 
                    strokeWidth={2}
                    name="Approved"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="denied" 
                    stroke={COLORS.denied} 
                    strokeWidth={2}
                    name="Denied"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <div className="grid gap-6 md:grid-cols-2">
            {/* Claims by Status */}
            <Card>
              <CardHeader>
                <CardTitle>Claims by Status</CardTitle>
                <CardDescription>Distribution of all claim statuses</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {statusData.map((entry, index) => {
                        const statusKey = entry.name.toLowerCase().replace(" ", "_") as keyof typeof COLORS;
                        return (
                          <Cell key={`cell-${index}`} fill={COLORS[statusKey] || "#8884d8"} />
                        );
                      })}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--background))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '6px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Claims by Type */}
            <Card>
              <CardHeader>
                <CardTitle>Claims by Type</CardTitle>
                <CardDescription>Breakdown of claim types</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={typeData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis 
                      dataKey="name" 
                      className="text-xs"
                      tick={{ fill: 'hsl(var(--foreground))' }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis 
                      className="text-xs"
                      tick={{ fill: 'hsl(var(--foreground))' }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--background))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '6px'
                      }}
                    />
                    <Bar dataKey="count" fill="hsl(var(--primary))" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Settlement Amounts Over Time */}
          <Card>
            <CardHeader>
              <CardTitle>Settlement Amounts Over Time</CardTitle>
              <CardDescription>Total and average settlement amounts by month</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={settlementData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="month" 
                    className="text-xs"
                    tick={{ fill: 'hsl(var(--foreground))' }}
                  />
                  <YAxis 
                    className="text-xs"
                    tick={{ fill: 'hsl(var(--foreground))' }}
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--background))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '6px'
                    }}
                    formatter={(value: number) => `$${value.toLocaleString()}`}
                  />
                  <Legend />
                  <Bar dataKey="total" fill="hsl(var(--primary))" name="Total Amount" />
                  <Bar dataKey="average" fill={COLORS.approved} name="Average Amount" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Status Trends Over Time */}
          <Card>
            <CardHeader>
              <CardTitle>Status Trends</CardTitle>
              <CardDescription>Claims by status over the last 6 months</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="month" 
                    className="text-xs"
                    tick={{ fill: 'hsl(var(--foreground))' }}
                  />
                  <YAxis 
                    className="text-xs"
                    tick={{ fill: 'hsl(var(--foreground))' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--background))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '6px'
                    }}
                  />
                  <Legend />
                  <Bar dataKey="pending" stackId="a" fill={COLORS.pending} name="Pending" />
                  <Bar dataKey="in_review" stackId="a" fill={COLORS.in_review} name="In Review" />
                  <Bar dataKey="approved" stackId="a" fill={COLORS.approved} name="Approved" />
                  <Bar dataKey="denied" stackId="a" fill={COLORS.denied} name="Denied" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Analytics;
